"use client";

import type React from "react";
import { Card, CardContent } from "@papier/ui/components/ui/card";
import { Braces } from "lucide-react";
import { SchemaFieldComponent } from "./schema-field";
import {
  DragState,
  SchemaField,
} from "@papier/grapesjs-editor/src/components/editor/schema-builder/types/schema";

interface SchemaCanvasProps {
  schema: SchemaField[];
  selectedField?: SchemaField | null;
  dragState?: DragState;
  readOnly?: boolean;
  onFieldSelect?: (field: SchemaField) => void;
  onFieldUpdate?: (id: string, updates: Partial<SchemaField>) => void;
  onFieldDelete?: (id: string) => void;
  onDragStart?: (e: React.DragEvent, field: SchemaField) => void;
  onDragEnter?: (
    e: React.DragEvent,
    targetId: string,
    position: "before" | "after" | "inside",
  ) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (
    e: React.DragEvent,
    targetId?: string,
    position?: "before" | "after" | "inside",
  ) => void;
  onAddField?: (type: SchemaField["type"]) => void;
  dragCounter?: React.RefObject<number>;
}

export function SchemaCanvas({
  schema,
  selectedField,
  dragState,
  readOnly = false,
  onFieldSelect,
  onFieldUpdate,
  onFieldDelete,
  onDragStart,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  onAddField,
  dragCounter,
}: SchemaCanvasProps) {
  const handleCanvasDrop = (e: React.DragEvent) => {
    if (readOnly || !onDrop) return;
    onDrop(e);
  };

  const handleEndZoneDrop = (e: React.DragEvent) => {
    if (readOnly || !onAddField) return;

    e.preventDefault();
    const dragData = e.dataTransfer.getData("text/plain");
    if (dragData.startsWith("new:")) {
      const fieldType = dragData.replace("new:", "") as SchemaField["type"];
      onAddField(fieldType);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      {schema.length === 0 ? (
        <Card
          className={`border-2 border-dashed transition-colors ${
            !readOnly && dragState?.isDragging ? "bg-secondary border-blue-400" : ""
          }`}
          onDragEnter={
            !readOnly
              ? (e) => {
                  e.preventDefault();
                  if (dragCounter) dragCounter.current++;
                }
              : undefined
          }
          onDragLeave={
            !readOnly
              ? (e) => {
                  e.preventDefault();
                  if (dragCounter) dragCounter.current--;
                }
              : undefined
          }
          onDragOver={
            !readOnly
              ? (e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "copy";
                }
              : undefined
          }
          onDrop={!readOnly ? handleCanvasDrop : undefined}
        >
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Braces className="mb-4 h-16 w-16" />
            <h3 className="mb-2 text-xl font-semibold">
              {readOnly ? "No Schema Defined" : "Start Building Your Schema"}
            </h3>
            <p className="mb-6 max-w-md text-center">
              {readOnly
                ? "This schema is empty."
                : "Drag field types from the sidebar to begin creating your data structure. Drag and drop to reorder fields."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1">
          {schema.map((field) => (
            <SchemaFieldComponent
              key={field.id}
              field={field}
              selectedField={selectedField}
              dragState={dragState}
              readOnly={readOnly}
              onSelect={onFieldSelect}
              onUpdate={onFieldUpdate}
              onDelete={onFieldDelete}
              onDragStart={onDragStart}
              onDragEnter={onDragEnter}
              onDragLeave={onDragLeave}
              onDragOver={onDragOver}
              onDrop={onDrop}
            />
          ))}
          {!readOnly && (
            <div
              className={`h-8 rounded transition-all ${
                dragState?.isDragging ? "border-2 border-dashed border-blue-400" : ""
              }`}
              onDragEnter={(e) => {
                e.preventDefault();
                if (dragCounter) dragCounter.current++;
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                if (dragCounter) dragCounter.current--;
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "copy";
              }}
              onDrop={handleEndZoneDrop}
            />
          )}
        </div>
      )}
    </div>
  );
}
